import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { getMeta, routeKeyFromPath, SITE_URL } from "../data/meta";

/** Ein <link>/<meta> im Head setzen oder anlegen. */
const setTag = (selector, create, attribute, value) => {
  let node = document.head.querySelector(selector);
  if (!node) {
    node = create();
    document.head.appendChild(node);
  }
  node.setAttribute(attribute, value);
};

const link = (rel, extra = {}) => () => {
  const node = document.createElement("link");
  node.setAttribute("rel", rel);
  Object.entries(extra).forEach(([key, val]) => node.setAttribute(key, val));
  return node;
};

/**
 * Hält Titel, Description, Sprache und Canonical/hreflang beim Navigieren
 * aktuell. Rendert nichts – die statische Fassung in index.html bleibt für
 * Crawler ohne JavaScript massgeblich.
 */
export const PageMeta = () => {
  const { language } = useLanguage();
  const { pathname } = useLocation();

  useEffect(() => {
    const { title, description } = getMeta(language, pathname);
    const route = routeKeyFromPath(pathname);
    const enUrl = `${SITE_URL}${route === "/" ? "/" : route}`;
    const deUrl = `${SITE_URL}/de${route === "/" ? "" : route}`;
    const canonical = language === "de" ? deUrl : enUrl;

    document.title = title;
    document.documentElement.lang = language;

    setTag('meta[name="description"]', () => {
      const node = document.createElement("meta");
      node.setAttribute("name", "description");
      return node;
    }, "content", description);

    setTag('link[rel="canonical"]', link("canonical"), "href", canonical);
    setTag('link[rel="alternate"][hreflang="en"]', link("alternate", { hreflang: "en" }), "href", enUrl);
    setTag('link[rel="alternate"][hreflang="de"]', link("alternate", { hreflang: "de" }), "href", deUrl);
    setTag('link[rel="alternate"][hreflang="x-default"]', link("alternate", { hreflang: "x-default" }), "href", enUrl);

    const ogContent = {
      "og:title": title,
      "og:description": description,
      "og:url": canonical,
      "og:locale": language === "de" ? "de_CH" : "en_US",
    };
    Object.entries(ogContent).forEach(([property, value]) => {
      setTag(`meta[property="${property}"]`, () => {
        const node = document.createElement("meta");
        node.setAttribute("property", property);
        return node;
      }, "content", value);
    });
  }, [language, pathname]);

  return null;
};

export default PageMeta;
